import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Dialog, TextField } from '../ui';
import { PlusIcon } from '../ui/icons';

/**
 * A new location type (its name and, optionally, its first places, all at
 * the top level: the tree nests them afterwards), or an existing one renamed
 * (its places are edited in the tree, Settings › Locations).
 */
function CreateLocationTypeModal({ onClose, onCreate, locationType }) {
  const { t } = useTranslation();
  const [name, setName] = useState(locationType ? locationType.name || '' : '');
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState('');
  const [busy, setBusy] = useState(false);

  const addLocation = () => {
    if (!currentLocation.trim()) return;
    setLocations([...locations, currentLocation.trim()]);
    setCurrentLocation('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await onCreate(locationType ? { name } : { name, locations });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={locationType ? t('locationTypeForm.editTitle') : t('locationTypes.create')}
      description={locationType ? t('locationTypeForm.placesInTree') : undefined}
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="location-type-form" disabled={!name.trim()} loading={busy}>
            {locationType ? t('locationTypeForm.update') : t('locationTypeForm.create')}
          </Button>
        </>
      )}
    >
      <form id="location-type-form" className="dialog-form" onSubmit={handleSubmit}>
        <TextField label={t('locationTypeForm.name')} value={name} onChange={(e) => setName(e.target.value)} required
                   placeholder={t('locationTypeForm.namePlaceholder')} />
        {!locationType && (
          <fieldset className="type-editor__options">
            <legend className="ui-field__label">{t('locationTypeForm.locations')}</legend>
            <div className="type-editor__add-option">
              <TextField label={t('locationTypeForm.newPlace')} hideLabel value={currentLocation}
                         placeholder={t('locationTypeForm.locationPlaceholder')}
                         onChange={(e) => setCurrentLocation(e.target.value)}
                         onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLocation(); } }} />
              <Button variant="secondary" icon={PlusIcon} onClick={addLocation} disabled={!currentLocation.trim()}>{t('locationTypeForm.add')}</Button>
            </div>
            {locations.length > 0 && (
              <ul className="tag-list" aria-label={t('locationTypeForm.defined', { count: locations.length })}>
                {locations.map((location, index) => (
                  <li key={`${location}-${index}`}>
                    <Chip onRemove={() => setLocations(locations.filter((_, i) => i !== index))}
                          removeLabel={t('locationTypeForm.removePlace', { name: location })}>{location}</Chip>
                  </li>
                ))}
              </ul>
            )}
            <p className="ui-field__hint">{t('locationTypeForm.help')}</p>
          </fieldset>
        )}
      </form>
    </Dialog>
  );
}

export default CreateLocationTypeModal;
